import React, { useEffect, useRef, useState, lazy, Suspense } from "react";
import { useLocation } from "react-router-dom";
import { RefreshAuthToken } from "services/users";
import { handleLogoutFn } from "common/helpers";
import { GetStateStorage } from "common/storage";
import { LOG_MODULES, getLogListgenUserID, trackException } from "common/errorTracker";
import {
  GetOAuthToken,
  IsValidLoginAuth,
  UpsertFullAuthTokenAndState,
} from "common/storage";

const REFRESH_WINDOW_MS = 60 * 60 * 1000;
const CHECK_INTERVAL_MS = 60 * 1000;
const SessionExpirationDialog = lazy(() => import("components/SessionExpirationDialog"));

const excludedPaths = [
  "/login",
  "/agent/register",
  "/register",
  "/password/forgot",
  "/password/reset",
  "/agent/open/website",
  "/agent/printroi",
  "/lp/schedule/presentation",
  "/printpma",
  "/pdfprintversion",
  "/agent/areas-by-city",
  "/agent/roi",
  "/validateAgentToken",
  "/lp/open/agent/roi",
  "/lp/open/agent/areas-by-city",
  "/404",
];

const SessionRefreshManager = () => {
  const location = useLocation();
  const refreshIntervalRef = useRef(null);
  const refreshInProgressRef = useRef(false);
  const [showSessionExpiredDialog, setShowSessionExpiredDialog] = useState(false);

  const clearRefreshInterval = () => {
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
      refreshIntervalRef.current = null;
    }
  };

  const isExcludedPath = () => {
    const currentPath = window.location.pathname || "";
    return excludedPaths.some((path) => currentPath.includes(path));
  };

  const getCurrentToken = () => {
    try {
      const tokenRaw = GetOAuthToken();
      return tokenRaw ? JSON.parse(tokenRaw) : null;
    } catch (e) {
      return null;
    }
  };

  const getTokenExpiryTimeMs = (tokenData) => {
    if (!tokenData || !tokenData.expires_in) {
      return null;
    }
    const expiresInSeconds = Math.abs(parseInt(tokenData.expires_in, 10));
    if (!expiresInSeconds || Number.isNaN(expiresInSeconds)) {
      return null;
    }
    const tokenReceivedAt = tokenData.token_received_at
      ? parseInt(tokenData.token_received_at, 10)
      : Date.now();
    return tokenReceivedAt + expiresInSeconds * 1000;
  };

  const runTokenRefresh = async () => {
    if (refreshInProgressRef.current) {
      return;
    }
    if (isExcludedPath() || !IsValidLoginAuth()) {
      return;
    }
    const tokenData = getCurrentToken();
    if (!tokenData) {
      return;
    }
    refreshInProgressRef.current = true;
    try {
      // refresh_token is in httpOnly cookie — CF Worker handles it, no arg needed
      const refreshedToken = await RefreshAuthToken();
      const updated = UpsertFullAuthTokenAndState(refreshedToken);
      if (!updated) {
        const stateData = GetStateStorage() || {};
        trackException({
          module: LOG_MODULES.SESSION_REFRESH,
          code: 'REFRESH_UPDATE_FAILED',
          error: new Error('Unable to update refreshed token.'),
          listgenUserID: getLogListgenUserID(
            stateData.userID,
            stateData.userData?.listgenUserID
          ),
          data: { hasSessionSwitch: Boolean(tokenData?.oldSessionData) },
        });
        throw new Error("Unable to update refreshed token.");
      }
      setShowSessionExpiredDialog(false);
      localStorage.setItem("LG_APP_FULL_AUTH_REFRESHED_AT", `${Date.now()}`);
    } catch (e) {
      const tokenExpiryTime = getTokenExpiryTimeMs(tokenData);
      const isExpired = tokenExpiryTime !== null && tokenExpiryTime <= Date.now();
      const stateData = GetStateStorage() || {};
      trackException({
        module: LOG_MODULES.SESSION_REFRESH,
        code: isExpired ? 'SESSION_EXPIRED' : 'REFRESH_FAILED',
        error: e,
        listgenUserID: getLogListgenUserID(
          stateData.userID,
          stateData.userData?.listgenUserID
        ),
        data: {
          isExpired,
          hasSessionSwitch: Boolean(tokenData?.oldSessionData),
        },
      });
      if (isExpired) {
        setShowSessionExpiredDialog(true);
      }
    } finally {
      refreshInProgressRef.current = false;
    }
  };

  const checkSessionAndRefresh = () => {
    if (isExcludedPath() || !IsValidLoginAuth()) {
      return;
    }
    const tokenData = getCurrentToken();
    if (!tokenData) {
      return;
    }
    const expiryTime = getTokenExpiryTimeMs(tokenData);
    if (expiryTime === null) {
      return;
    }
    const timeUntilExpiry = expiryTime - Date.now();
    if (timeUntilExpiry <= 0) {
      const stateData = GetStateStorage() || {};
      trackException({
        module: LOG_MODULES.SESSION_REFRESH,
        code: 'SESSION_EXPIRED',
        error: new Error('Access token expired'),
        listgenUserID: getLogListgenUserID(
          stateData.userID,
          stateData.userData?.listgenUserID
        ),
        data: { hasSessionSwitch: Boolean(tokenData?.oldSessionData) },
      });
      setShowSessionExpiredDialog(true);
      return;
    }

    if (timeUntilExpiry <= REFRESH_WINDOW_MS) {
      runTokenRefresh();
    }
  };

  useEffect(() => {
    checkSessionAndRefresh();

    clearRefreshInterval();
    refreshIntervalRef.current = setInterval(checkSessionAndRefresh, CHECK_INTERVAL_MS);
    const storageListener = (event) => {
      if (
        event.key === "LG_APP_FULL_AUTH_REFRESHED_AT" ||
        event.key === "LG_APP_STATE_DATA" ||
        event.key === "LG_APP_TOKEN_META"
      ) {
        checkSessionAndRefresh();
      }
    };
    window.addEventListener("storage", storageListener);
    return () => {
      window.removeEventListener("storage", storageListener);
      clearRefreshInterval();
    };
  }, [location.pathname]);

  if (showSessionExpiredDialog) {
    return (
      <Suspense fallback={<div>Loading...</div>}>
        <SessionExpirationDialog
          open={true}
          storageType="session"
          onSessionExpired={() => handleLogoutFn(6)}
          onClose={() => setShowSessionExpiredDialog(false)}
        />
      </Suspense>
    );
  }

  return null;
};

export default SessionRefreshManager;
